// garmentApplyMenu — the rules behind a garment card's "Apply to character"
// submenu (ENG-1108). Pure, so the states it can be in are asserted by
// `node --test` rather than eyeballed in three different situations.
//
// Why a submenu naming ONE character rather than a picker over all of them:
// wearing a garment is a runtime operation on the avatar that is mounted. There
// is one scene and one avatar in it, so "apply to..." has exactly one real
// destination, and offering a list of characters you are not looking at would
// promise a thing the app cannot do — it would have to switch character first,
// which is a different action with its own cost.
//
// So the submenu lists the character IN THE SCENE. When there isn't one it
// still renders, disabled, saying so: a control that vanishes teaches nothing
// about why (the rule voiceInputState.js sets out and AssetContextMenu's
// Download item already follows).

/**
 * What the "Apply to character ▸" submenu should show for one garment card.
 *
 * @param {object} args
 * @param {string|null} args.characterId   the character the browser is scoped to
 * @param {string|null} args.characterName its display name, when known
 * @param {object|null} args.worn          garmentSlot's current garment
 * @param {string|null} args.assetId       this card's server id
 * @returns {{targets: Array<{id: string, label: string, disabled: boolean,
 *            reason: string|null, active: boolean}>,
 *           canRemove: boolean, isWorn: boolean}}
 */
export function garmentApplyTargets({ characterId, characterName, worn, assetId }) {
  const isWorn = !!(assetId && worn?.assetId && String(worn.assetId) === String(assetId))

  if (!characterId) {
    return {
      isWorn: false,
      canRemove: false,
      targets: [{
        id: '',
        label: 'No character in the scene',
        disabled: true,
        // Naming the fix, not just the state.
        reason: 'Open a character first — a garment is worn by the avatar that is loaded.',
        active: false,
      }],
    }
  }

  return {
    isWorn,
    // Only offered when THIS card is the garment on — "Remove" on a card that
    // is not worn would either do nothing or take off someone else's garment,
    // and both are worse than the item not being there.
    canRemove: isWorn,
    targets: [{
      id: characterId,
      label: characterName || characterId,
      // Re-applying the garment already on is a no-op; disabled and labelled as
      // "worn" says so before the click rather than after it.
      disabled: isWorn,
      reason: isWorn ? 'Already worn' : null,
      active: isWorn,
    }],
  }
}

/** The status line after an apply — one wording, wherever it is reported. */
export function describeGarmentApply(name, characterLabel) {
  return `Wearing ${name} on ${characterLabel}`
}
